// ─── Member-side org reads with a per-org cache ───
import { useState, useEffect, useCallback, useRef } from 'react';
import { LS } from './storage';
import { today } from './helpers';
import { useOrg, orgApi, orgErr, onOrgPing, isActiveOrg } from './org';
import { isCoachingClient } from './coachForms';

const FOREGROUND_REFRESH_MS = 15000;

// One member-side org read (`my_forms`, `my_habits`, `my_nutrition_targets`,
// `list_org_exercises`): cached per org for an instant first paint, refetched on mount,
// on a realtime ping of `kind` and when the app comes back to the foreground (at most
// every `staleMs`). Clients of an active org only, unless `anyRole` (staff too).
// `canApply()` lets a caller hold back a fetched response while its own optimistic
// writes are in flight.
export function useOrgFeed(action, { kind = null, cacheKey, canApply, anyRole = false, staleMs = FOREGROUND_REFRESH_MS }) {
  const { org } = useOrg();
  const enabled = anyRole ? isActiveOrg(org) : isCoachingClient(org);
  const orgId = enabled ? org.org.id || 'org' : null;
  const readCache = useCallback(() => {
    const c = LS.get(cacheKey);
    return orgId && c && c.org_id === orgId && c.data ? c.data : null;
  }, [orgId, cacheKey]);
  const [data, setData] = useState(readCache);
  const seq = useRef(0);
  const lastLoad = useRef(0);
  const applyRef = useRef(canApply);
  applyRef.current = canApply;

  const load = useCallback(async () => {
    if (!orgId) return;
    const my = ++seq.current;
    lastLoad.current = Date.now();
    try {
      const r = await orgApi(action, { today: today() });
      if (my !== seq.current || (applyRef.current && !applyRef.current())) return;
      setData(r && typeof r === 'object' ? r : null);
    } catch (e) {
      if (my !== seq.current) return;
      const er = orgErr(e);
      // A refusal (not a member any more, feature missing on an older API) clears the card;
      // offline, rate limits and server errors keep what was loaded last.
      if (er.status >= 400 && er.status < 500 && ![401, 408, 423, 429].includes(er.status)) setData(null);
    }
  }, [orgId, action]);

  useEffect(() => { setData(readCache()); if (orgId) load(); }, [orgId, readCache, load]);
  useEffect(() => {
    if (!orgId) return;
    if (data) LS.set(cacheKey, { org_id: orgId, data }); else LS.del(cacheKey);
  }, [data, orgId, cacheKey]);
  useEffect(() => onOrgPing((p) => { if (kind && p && (Array.isArray(kind) ? kind.includes(p.kind) : p.kind === kind)) load(); }), [kind, load]);
  useEffect(() => {
    if (!orgId) return undefined;
    const vis = () => { if (document.visibilityState === 'visible' && Date.now() - lastLoad.current > staleMs) load(); };
    document.addEventListener('visibilitychange', vis);
    return () => document.removeEventListener('visibilitychange', vis);
  }, [orgId, load, staleMs]);

  return { enabled, data, setData, load };
}
